import React from 'react';

import { ReactComponent as FemaleIcon } from '../../assets/icons/stroke/female.svg';
import { ReactComponent as MaleIcon } from '../../assets/icons/stroke/male.svg';
import { getGenderColor } from '../../helpers/genderDisplay';
import { cn } from '../../lib/utils';

export type ProfileGender = 'female' | 'male';

type ProfileGenderIconProps = {
  gender?: ProfileGender | null;
  profileId?: number | string | null;
  profileStatus?: string | null;
  size?: number;
  includeEndSpacing?: boolean;
  className?: string;
};

export const verifiedProfileGender = ({
  gender,
  profileId,
  profileStatus,
}: Pick<ProfileGenderIconProps, 'gender' | 'profileId' | 'profileStatus'>): ProfileGender | null => {
  const numericProfileId =
    profileId === null || profileId === undefined ? null : Number(profileId);

  if (
    (gender !== 'female' && gender !== 'male') ||
    profileStatus?.toUpperCase() === 'SHADOW' ||
    (numericProfileId !== null && Number.isFinite(numericProfileId) && numericProfileId < 0)
  ) {
    return null;
  }

  return gender;
};

const ProfileGenderIcon: React.FC<ProfileGenderIconProps> = ({
  gender,
  profileId,
  profileStatus,
  size = 14,
  includeEndSpacing = true,
  className,
}) => {
  const displayGender = verifiedProfileGender({
    gender,
    profileId,
    profileStatus,
  });

  if (!displayGender) return null;

  const GenderIcon = displayGender === 'female' ? FemaleIcon : MaleIcon;
  const label = displayGender === 'female' ? 'Female' : 'Male';

  return (
    <span
      role="img"
      aria-label={label}
      className={cn('inline-flex shrink-0 items-center justify-center', className)}
      style={{
        color: getGenderColor(displayGender),
        height: size,
        marginRight: includeEndSpacing ? (displayGender === 'female' ? 2 : 4) : 0,
        width: size,
      }}
    >
      <GenderIcon
        aria-hidden="true"
        focusable="false"
        className="h-full w-full [&_path]:fill-current"
      />
    </span>
  );
};

export default ProfileGenderIcon;
